import { type KeyboardEvent, type MouseEvent, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { PencilIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { cn } from '@air/utils-tailwind';

import { type Skill, SkillExplanation, SkillTooltipSurface } from '../../skills';
import { skillCommandChip } from './SkillCommandNode';

export type SkillSuggestionItemProps = {
  id: string;
  index: number;
  item: Skill;
  onMouseEnter: (index: number) => void;
  onSelect: (index: number) => void;
  selected: boolean;
  /** When true, renders a pencil affordance that opens the edit-skill modal. */
  canEdit?: boolean;
  /** Opens the host's edit-skill modal for this skill. */
  onEdit?: (skill: Skill) => void;
};

export const SkillSuggestionItem = memo(
  ({ canEdit = false, id, index, item, onEdit, onMouseEnter, onSelect, selected }: SkillSuggestionItemProps) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);

    const handleClick = useCallback(() => onSelect(index), [onSelect, index]);
    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(index);
        }
      },
      [onSelect, index],
    );
    const handleMouseEnter = useCallback(() => onMouseEnter(index), [onMouseEnter, index]);

    useIsomorphicLayoutEffect(() => {
      if (!item.description) {
        setIsDescriptionTruncated(false);
        return;
      }

      const element = descriptionRef.current;

      // biome-ignore lint/suspicious/noUnnecessaryConditions: the ref is populated by React when the description div below mounts; Biome only sees the initial `null`.
      if (!element) {
        setIsDescriptionTruncated(false);
        return;
      }

      const updateTruncation = () => {
        setIsDescriptionTruncated(element.scrollWidth > element.clientWidth);
      };

      updateTruncation();

      const observer = new ResizeObserver(updateTruncation);
      observer.observe(element);

      return () => {
        observer.disconnect();
      };
    }, [item.description]);

    const handleEditClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        // Stop the row's select/insert handler from firing when editing.
        event.stopPropagation();
        onEdit?.(item);
      },
      [onEdit, item],
    );
    const handleEditKeyDown = useCallback((event: KeyboardEvent<HTMLButtonElement>) => {
      // Keyboard-activating the pencil (Enter/Space) natively fires its onClick; stop the keydown
      // from bubbling so the row's handler doesn't also select the skill.
      if (event.key === 'Enter' || event.key === ' ') {
        event.stopPropagation();
      }
    }, []);

    useEffect(() => {
      if (selected) {
        itemRef.current?.scrollIntoView({ block: 'nearest' });
      }
    }, [selected]);

    const showEdit = canEdit && !!onEdit;
    // Force closed when the description fits; otherwise keep the selected row open and hover-open the rest.
    const tooltipOpen = isDescriptionTruncated ? selected || undefined : false;

    return (
      <SkillTooltipSurface
        delayDuration={200}
        label={<SkillExplanation skill={item} />}
        open={tooltipOpen}
        side="right"
      >
        <div // NOSONAR -- APG listbox option; native <option> cannot render the command chip and description
          aria-selected={selected}
          className={cn(
            'flex w-full cursor-pointer items-start gap-1 overflow-hidden rounded-4 px-2 py-1.5 text-left ads:text-primary text-14 text-gray-12 outline-none hover:ads:bg-hover-subtle hover:bg-gray-4 focus-visible:ring-2 focus-visible:ring-blue-9 active:ads:bg-ads-gray-5 active:bg-gray-5',
            selected && 'ads:bg-hover-subtle bg-gray-4',
          )}
          data-slash-command={item.slashCommand}
          data-testid="SKILL_SUGGESTION_OPTION"
          id={id}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onMouseEnter={handleMouseEnter}
          ref={itemRef}
          role="option"
          tabIndex={-1}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className={cn(skillCommandChip(), 'max-w-full truncate')}>/{item.slashCommand}</div>
            {item.description ? (
              <div className="truncate font-normal ads:text-metadata text-12 text-gray-10" ref={descriptionRef}>
                {item.description}
              </div>
            ) : null}
          </div>
          {showEdit && (
            <IconButton
              adsIcon
              appearance="ghost"
              className="shrink-0"
              color="gray"
              icon={PencilIcon}
              label={`Edit ${item.name}`}
              onClick={handleEditClick}
              onKeyDown={handleEditKeyDown}
              // Roving tabindex: only the highlighted row's pencil is tabbable, so tabbing from the
              // composer reaches the edit control for the arrow-selected skill (not the first in DOM order).
              size="small"
              tabIndex={selected ? 0 : -1}
              type="button"
            />
          )}
        </div>
      </SkillTooltipSurface>
    );
  },
);

SkillSuggestionItem.displayName = 'SkillSuggestionItem';
